import Image from "next/future/image";

import { useFloating, shift, autoUpdate, offset } from "@floating-ui/react-dom";

import TeamPhotoOne from "assets/careers/team-photos/team-photo-1@1080w.png";
import TeamPhotoTwo from "assets/careers/team-photos/team-photo-2@1080w.png";

import SectionHeader from "./SectionHeader";

import styles from "./Team.module.css";

type TeamMemberType = {
  name: string;
  initials: string;
  position: string;
  pronouns: string;
  colorFrom: string;
  colorTo: string;
  bio: string;
};

const team: TeamMemberType[] = [
  {
    name: "Thomas Mann",
    initials: "TPM",
    position: "CEO & Co-Founder",
    pronouns: "he/him",
    colorFrom: "#FF6363",
    colorTo: "#D72A2A",
    bio: "Previously worked at Meta, working on Spark AR as a Software Engineer.\n\nBefore Raycast, toured through Europe as a DJ.",
  },
  {
    name: "Petr Nikolaev",
    initials: "PN",
    position: "CTO & Co-Founder",
    pronouns: "he/him",
    colorFrom: "#86DCC2",
    colorTo: "#0F3D34",
    bio: "Previously worked at Meta as a Software Engineer on Spark AR and WhatsApp.\n\nWas a professional poker player before started a normal job as a developer.",
  },
  {
    name: "Nichlas Wærnes Andersen",
    initials: "NWA",
    position: "Product Designer",
    pronouns: "he/him",
    colorFrom: "#CF2F98",
    colorTo: "#9A1B6E",
    bio: "Previously worked as a freelance designer.\n\nIs officially Raycast’s first employee.",
  },
  {
    name: "Felix Raab",
    initials: "FR",
    position: "Software Engineer",
    pronouns: "",
    colorFrom: "#6A6D72",
    colorTo: "#303337",
    bio: "Previously worked in Tech Consulting, at IBM, and in Academia.\n\nStarted personal computing on a VC-20.",
  },
  {
    name: "Artem Konovalov",
    initials: "AK",
    position: "Software Engineer",
    pronouns: "he/him",
    colorFrom: "#0085FF",
    colorTo: "#003EB7",
    bio: "Previously worked as a Software Engineer at Capture.chat, Prisma AI, Sports.ru, Jarvis, and a few other projects.\n\nNotoriously hates socks.",
  },
  {
    name: "Remon Oldenbeuving",
    initials: "RSO",
    position: "Software Engineer",
    pronouns: "he/him",
    colorFrom: "#FF6363",
    colorTo: "#D72A2A",
    bio: "Previously an Engineering Manager at HackerOne.",
  },
  {
    name: "Grzegorz Krukowski",
    initials: "GK",
    position: "Software Engineer",
    pronouns: "he/him",
    colorFrom: "#FF9217",
    colorTo: "#C75D07",
    bio: "Previously worked at Memorado, Goodbeans, and Outfittery as an iOS Developer.\n\nHas worked in the gaming industry for most of his career.",
  },
  {
    name: "Stuart Regan",
    initials: "SR",
    position: "Product Designer",
    pronouns: "he/him",
    colorFrom: "#A485FF",
    colorTo: "#6A3DEC",
    bio: "Previously a Product Designer at Monzo and Cuvva.\n\nWas an electrician for one day, and has been a product designer ever since.",
  },
  {
    name: "Mohammed Imthathullah",
    initials: "MI",
    position: "Software Engineer",
    pronouns: "he/him",
    colorFrom: "#FF9217",
    colorTo: "#C75D07",
    bio: "Previously a Software Engineer at Zoho, and Cognizant.\n\nLoves Test Cricket – probably the only game in the world played over 5 days.",
  },
  {
    name: "John Buckley",
    initials: "JB",
    position: "macOS Engineer",
    pronouns: "he/him",
    colorFrom: "#CF2F98",
    colorTo: "#9A1B6E",
    bio: "Previously a Software Engineer at Colortrix, Olive Toast Software, and Crocodile Clips.\n\nCannot go a single day without marmalade.",
  },
  {
    name: "Marcos Sánchez-Dehesa",
    initials: "MSD",
    position: "Software Engineer",
    pronouns: "he/him",
    colorFrom: "#0085FF",
    colorTo: "#003EB7",
    bio: "Previously a Software Engineer at Kitchen Stories, and Relayr.\n\nWould do anything for a good black paella with alioli.",
  },
  {
    name: "Sandy Krupkova",
    initials: "SK",
    position: "People Operations Manager",
    pronouns: "she/her",
    colorFrom: "#FF9217",
    colorTo: "#C75D07",
    bio: "Previously a HR Generalist, EMEA at Qualys.\n\nIs a junior level astronomy geek.",
  },
  {
    name: "Mathieu Dutour",
    initials: "MD",
    position: "API Engineer",
    pronouns: "he/him",
    colorFrom: "#A485FF",
    colorTo: "#6A3DEC",
    bio: "Previously an Engineer at GitBook, Sketch, and Enki.\n\nLearned how to ski, before knowing how to run.",
  },
  {
    name: "Sorin Muntean",
    initials: "SCM",
    position: "Software Engineer",
    pronouns: "he/him",
    colorFrom: "#86DCC2",
    colorTo: "#0F3D34",
    bio: "Previously a Software Engineer at Ottonova, and Internations.org.\n\nUsed to drive an ambulance for the Red Cross.",
  },
  {
    name: "Per Nielsen Tikær",
    initials: "PNT",
    position: "Technical Community Manager",
    pronouns: "he/him",
    colorFrom: "#6A6D72",
    colorTo: "#303337",
    bio: "Previously a Software Engineer at Klean.\n\nAn educated truck driver who made his first homepage back in 1997.",
  },
  {
    name: "Alexandr Subbotin",
    initials: "AS",
    position: "Software Engineer",
    pronouns: "he/him",
    colorFrom: "#8332a8",
    colorTo: "#324aa8",
    bio: "Previously CTO at Appbooster, creator of Cerebro app.\n\nJunior FPV-drone pilot.",
  },
  {
    name: "Daniel Sequeira",
    initials: "DS",
    position: "Engineering Manager",
    pronouns: "he/him",
    colorFrom: "#0085FF",
    colorTo: "#003EB7",
    bio: "Previously Engineering Manager at Pento, MessageBird and Bynder.\n\nWas a roller hockey player for 17 years.",
  },
];

const TeamMember = ({ name, position, bio, colorFrom, colorTo, initials }: TeamMemberType) => {
  const { x, y, reference, floating, strategy } = useFloating({
    placement: "top",
    whileElementsMounted: autoUpdate,
    middleware: [shift(), offset(10)],
  });
  return (
    <div key={name} className={styles.member} ref={reference}>
      <div className={styles.details} ref={floating} style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}>
        <strong className={styles.name}>{name}</strong>
        <span className={styles.position}>{position}</span>
        {bio.split("\n\n").map((bioLine, index) => (
          <p key={index} className={styles.bio}>
            {bioLine}
          </p>
        ))}
      </div>
      <div
        className={styles.initialsWrapper}
        style={
          {
            "--gradient-from": colorFrom,
            "--gradient-to": colorTo,
          } as React.CSSProperties
        }
      >
        <div className={styles.initialsBorder}></div>
        <span className={styles.inintials}>{initials}</span>
      </div>
    </div>
  );
};

const Team = () => (
  <div className={styles.team}>
    <SectionHeader title="Team" anchor="team">
      We’re a team who previously helped build products for millions of people across the internet at Spotify, Facebook,
      Zoho, Google, HackerOne, Notion, and more.
    </SectionHeader>

    <div className={styles.members}>
      {team.map((teamMember) => (
        <TeamMember key={teamMember.initials} {...teamMember} />
      ))}
    </div>

    <div className={styles.teamDetailsWrapper}>
      <div className={styles.photosWrapper}>
        <Image
          src={TeamPhotoTwo}
          className={styles.photoTwo}
          sizes={"(min-width: 620px) 540px, calc(96vw - 36px)"}
          alt=""
          priority
        />
        <Image
          src={TeamPhotoOne}
          className={styles.photoOne}
          sizes={"(min-width: 620px) 540px, calc(96vw - 36px)"}
          alt=""
          priority
        />
      </div>
      <div>
        <div className={styles.teamDetails}>
          <div>
            <div className={styles.detailsNumberWrapper}>
              <span className={styles.detailsNumber}>{team.length}</span>
            </div>
            <span className={styles.detailsLabel}>Folks so far</span>
          </div>
          <div>
            <div className={styles.detailsNumberWrapper}>
              <span className={styles.detailsNumber}>9</span>
            </div>
            <span className={styles.detailsLabel}>Countries</span>
          </div>
          <div>
            <div className={styles.detailsNumberWrapper}>
              <span className={styles.detailsNumber}>9</span>
            </div>
            <span className={styles.detailsLabel}>Raypets</span>
          </div>
          <div>
            <div className={styles.detailsNumberWrapper}>
              <span className={styles.detailsNumber}>100%</span>
            </div>
            <span className={styles.detailsLabel}>Remote</span>
          </div>
        </div>
        <div className={styles.aboutTeam}>
          <p>
            Our team is spread across 9 countries, 3 timezones, 2 continents, and we believe working fast, simple, and
            transparent, leads to a higher quality level of work, where we trust and respect our colleagues.{" "}
          </p>
          <p>
            Despite being fully-remote, we’ve still found time to meet up in our yearly retreats, such as Dubai 2021
            (left), and bond over food, drink, and activities like golf and quad-biking.
          </p>
        </div>
      </div>
    </div>
  </div>
);

export default Team;
